import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { IBadgeCellRendererParams } from '~/types/badge'
import type { ITemplateRowData } from '~/types/template'

import LandscapeSVG from '~/assets/landscape.svg'
import PortraitSVG from '~/assets/portrait.svg'
import ActionMenu from '~/components/ActionMenu'
import BadgeCellRenderer from '~/components/CellRenderers/BadgeCellRenderer'
import TemplateKeyCellRenderer from '~/components/CellRenderers/TemplateKeyCellRenderer'
import TemplateStatusCellRenderer from '~/components/CellRenderers/TemplateStatusCellRenderer'
import TemplateTypeCellRenderer from '~/components/CellRenderers/TemplateTypeCellRenderer'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import sharedHeaderTemplate from '~/components/HeaderTemplate'
import { TEMPLATE_STATUS } from '~/types/template'
import { valueComparator } from '~/utils/comparators'
import { copyText } from '~/utils/navigator'
import { actionsColDef, badgeColDef, lastUpdateColDef, platformColDef, textColDef } from '~/utils/table'
import { getTemplateFromKey } from '~/utils/template'

const OrientationCellRenderer = ({ value }: ICellRendererParams): ReactElement => {
  return value ? (
    <div>
      {(value === 'landscape' || value === 'both') && <img src={LandscapeSVG} />}
      {(value === 'portrait' || value === 'both') && <img src={PortraitSVG} />}
    </div>
  ) : (
    <div />
  )
}

const ActionsCellRenderer = ({ context, data }: ICellRendererParams<ITemplateRowData>): ReactElement => {
  if (data) {
    const { handleActivateTemplate, handleCreatePageContainer, handleDownloadWorkspace } = context

    const actions = [
      {
        action: () => copyText(data.templateKey, 'The template key has just been copied to the clipboard'),
        label: 'Copy template key',
      },
    ]

    if (data.status === TEMPLATE_STATUS.DRAFT) {
      actions.push({
        action: () => handleActivateTemplate(data),
        label: 'Activate',
      })
    }

    if (getTemplateFromKey(data.templateKey)) {
      actions.push({
        action: () => handleCreatePageContainer(data),
        label: 'Create page container',
      })
    }

    if (data.platform_dependant_properties?.workspace_file_id) {
      actions.push({
        action: () => handleDownloadWorkspace(data.platform_dependant_properties?.workspace_file_id),
        label: 'Download',
      })
    }

    return actions.length > 0 ? <ActionMenu menu={{ actions }} placement="top" /> : <div />
  }

  return <div />
}

const columnMinWidth = 100

const columns: ColDef<ITemplateRowData>[] = [
  {
    cellRenderer: TemplateKeyCellRenderer,
    comparator: valueComparator,
    field: 'title',
    flex: 1,
    headerComponentParams: {
      template: sharedHeaderTemplate({
        color: 'grey',
        headerSubtitle: 'Key',
        headerSubtitleClassname: 'capitalized',
        headerTitle: 'Title',
      }),
    },
    headerName: '',
    minWidth: 350,
    suppressHeaderMenuButton: true,
  },
  {
    cellRenderer: TemplateStatusCellRenderer,
    field: 'status',
    headerName: 'Status',
    minWidth: 110,
    suppressHeaderMenuButton: true,
    width: 110,
  },
  textColDef(TextCellRenderer, {
    field: 'version',
    headerName: 'Version',
  }),
  platformColDef({
    field: 'deviceType',
    minWidth: columnMinWidth,
    suppressHeaderMenuButton: true,
  }),
  {
    cellRenderer: OrientationCellRenderer,
    field: 'orientation',
    headerName: 'Orientation',
    minWidth: columnMinWidth,
    suppressHeaderMenuButton: true,
  },
  badgeColDef(BadgeCellRenderer, {
    autoHeight: true,
    field: 'capabilities',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['clear'],
    },
    filterValueGetter: ({ data }: ValueGetterParams<ITemplateRowData>): string[] | undefined => data?.capabilities,
    headerName: 'Capabilities',
    menuTabs: ['filterMenuTab'],
    minWidth: 150,
    valueGetter: ({ data }: ValueGetterParams<ITemplateRowData>): IBadgeCellRendererParams | undefined =>
      data
        ? {
            data: data.capabilities?.map((name) => ({ id: name, name })),
            type: 'default',
          }
        : undefined,
  }),
  {
    cellRenderer: TemplateTypeCellRenderer,
    field: 'type',
    headerName: 'Type',
    minWidth: columnMinWidth,
    suppressHeaderMenuButton: true,
  },
  lastUpdateColDef({
    field: 'updated_by',
  }),
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
